import { todayISO, type Transaction } from '@keel/core';
import { withLockConfirm } from '@/lib/locked';
import { useCallback } from 'react';
import { errorMessage, useTRPCClient } from '@/api/query';
import { toast, toastUndo } from '@/components/ui';
import { useRefresh } from './data';
import { plural } from './labels';

interface Learned {
  readonly payeeId: string; readonly payeeName: string; readonly categoryId: string; readonly defaultSet: boolean;
  readonly changes: readonly { id: string; payeeId: string | null; categoryId: string | null; transferAccountId: string | null; linked?: boolean }[];
}

/** `transactions.create` input that recreates `t` (server-owned fields dropped; reconciled becomes cleared). */
export const toCreateInput = (t: Transaction) => ({
  entityId: t.entityId, accountId: t.accountId, date: t.date, amountCents: t.amountCents, payeeId: t.payeeId, payeeRaw: t.payeeRaw,
  memo: t.memo, reference: t.reference, status: t.status === 'reconciled' ? ('cleared' as const) : t.status, isReviewed: t.isReviewed,
  originalAmountCents: t.originalAmountCents, originalCurrency: t.originalCurrency, fxRate: t.fxRate,
  splits: t.splits.map(({ categoryId, amountCents, memo, tagIds, gstCents, isTaxDeductible }) => ({ categoryId, amountCents, memo, tagIds, gstCents, isTaxDeductible })),
});

/** Transfer input for a leg (direction from its sign). */
const toTransferInput = (t: Transaction, date = t.date) => ({
  entityId: t.entityId,
  fromAccountId: t.amountCents < 0 ? t.accountId : (t.transferAccountId as string),
  toAccountId: t.amountCents < 0 ? (t.transferAccountId as string) : t.accountId,
  date, amountCents: Math.abs(t.amountCents), ...(t.memo ? { memo: t.memo } : {}),
});

/**
 * Undoable register actions shared by keyboard shortcuts, the bulk toolbar and the editor. Each one runs the
 * mutation(s), refreshes, and offers Undo that restores the previous state.
 */
export const useTxActions = () => {
  const client = useTRPCClient();
  const refresh = useRefresh();

  const fail = (e: unknown) => toast.error(errorMessage(e));

  const recreate = useCallback(
    async (rows: readonly Transaction[]) => {
      const seen = new Set<string>();
      for (const t of rows) {
        if (t.transferId && t.transferAccountId) {
          if (seen.has(t.transferId)) continue;
          seen.add(t.transferId);
          await client.transactions.createTransfer.mutate(toTransferInput(t));
        } else await client.transactions.create.mutate(toCreateInput(t));
      }
    },
    [client],
  );

  const remove = useCallback(
    async (rows: readonly Transaction[]) => {
      if (!rows.length) return;
      const removed: Transaction[] = [];
      const gone = new Set<string>();
      try {
        for (const t of rows) {
          if (gone.has(t.id)) continue;
          const r = await withLockConfirm((flag) => client.transactions.remove.mutate({ id: t.id, ...flag }));
          r.removedIds.forEach((id) => gone.add(id));
          removed.push(t);
        }
      } catch (e) {
        fail(e);
      }
      await refresh();
      if (removed.length) {
        toastUndo(removed.length === 1 ? 'Transaction deleted' : `${plural(removed.length, 'transaction')} deleted`, () => {
          recreate(removed).then(refresh, fail);
        });
      }
    },
    [client, recreate, refresh],
  );

  const duplicate = useCallback(
    async (t: Transaction) => {
      try {
        const today = todayISO();
        const created = t.transferId && t.transferAccountId
          ? (await client.transactions.createTransfer.mutate(toTransferInput(t, today)))[0]
          : await client.transactions.create.mutate({ ...toCreateInput(t), date: today, status: 'cleared', isReviewed: true });
        await refresh();
        toastUndo('Duplicated for today', () => {
          client.transactions.remove.mutate({ id: created.id }).then(refresh, fail);
        });
        return created;
      } catch (e) {
        fail(e);
        return null;
      }
    },
    [client, refresh],
  );

  const markReviewed = useCallback(
    async (rows: readonly Transaction[]) => {
      const ids = rows.filter((t) => !t.isReviewed).map((t) => t.id);
      if (!ids.length) {
        toast('Already reviewed');
        return;
      }
      try {
        await client.transactions.markReviewed.mutate({ ids });
        await refresh();
        toastUndo(`Marked ${plural(ids.length, 'transaction')} as reviewed`, () => {
          client.transactions.bulkUpdate.mutate({ ids, patch: { isReviewed: false } }).then(refresh, fail);
        });
      } catch (e) {
        fail(e);
      }
    },
    [client, refresh],
  );

  /** "Also categorised 12 other Fitness Passport transactions · Undo" after a hand categorisation taught a payee. */
  const announceLearned = useCallback(
    (entityId: string, learned: readonly Learned[] | undefined) => {
      const moved = (learned ?? []).filter((l) => l.changes.length > 0);
      if (!moved.length) return;
      const n = moved.reduce((a, l) => a + l.changes.length, 0);
      const who = moved.length === 1 ? `${moved[0]!.payeeName} ` : '';
      toastUndo(`Also categorised ${plural(n, `other ${who}transaction`)}`, () => {
        client.transactions.undoAuto.mutate({
          entityId, changes: moved.flatMap((l) => l.changes),
          payeeDefaults: moved.filter((l) => l.defaultSet).map((l) => ({ payeeId: l.payeeId, categoryId: l.categoryId })),
        }).then(refresh, fail);
      }, `${moved.length === 1 ? moved[0]!.payeeName : 'These payees'} will use this category from now on.`);
    },
    [client, refresh],
  );

  const categorise = useCallback(
    async (rows: readonly Transaction[], categoryId: string | null, categoryName: string) => {
      const editable = rows.filter((t) => !t.transferId);
      if (!editable.length) {
        toast('Transfers don’t take a category');
        return;
      }
      try {
        const r = await client.transactions.bulkUpdate.mutate({ ids: editable.map((t) => t.id), patch: { categoryId, isReviewed: true } });
        await refresh();
        toastUndo(`${plural(editable.length, 'transaction')} moved to ${categoryName}`, () => {
          Promise.all(editable.map((t) => client.transactions.update.mutate({ id: t.id, splits: t.splits, isReviewed: t.isReviewed, learn: false })))
            .then(refresh, fail);
        });
        announceLearned(editable[0]!.entityId, r.learned);
      } catch (e) {
        fail(e);
      }
    },
    [client, refresh, announceLearned],
  );

  const setStatus = useCallback(
    async (rows: readonly Transaction[], status: 'cleared' | 'pending') => {
      const editable = rows.filter((t) => t.status !== 'reconciled' && t.status !== status);
      if (!editable.length) return;
      try {
        await client.transactions.bulkUpdate.mutate({ ids: editable.map((t) => t.id), patch: { status } });
        await refresh();
        toastUndo(`Marked ${plural(editable.length, 'transaction')} as ${status}`, () => {
          Promise.all(editable.map((t) => client.transactions.update.mutate({ id: t.id, status: t.status })))
            .then(refresh, fail);
        });
      } catch (e) {
        fail(e);
      }
    },
    [client, refresh],
  );

  const addTag = useCallback(
    async (rows: readonly Transaction[], tagId: string, tagName: string) => {
      try {
        await client.transactions.bulkUpdate.mutate({ ids: rows.map((t) => t.id), patch: { addTagIds: [tagId] } });
        await refresh();
        toastUndo(`Tagged ${plural(rows.length, 'transaction')} #${tagName}`, () => {
          Promise.all(rows.map((t) => client.transactions.update.mutate({ id: t.id, splits: t.splits }))).then(refresh, fail);
        });
      } catch (e) {
        fail(e);
      }
    },
    [client, refresh],
  );

  /** Backfill: payees, own transfers and confident categories for every uncategorised row; one Undo puts it all back. */
  const autoCategorise = useCallback(
    async (entityId: string) => {
      try {
        const r = await client.transactions.autoCategorise.mutate({ entityId });
        await refresh();
        const parts = [
          r.categorised ? `${plural(r.categorised, 'transaction')} categorised` : null,
          r.transfers ? `${plural(r.transfers, 'transfer')} matched` : null,
          r.awaiting ? `${plural(r.awaiting, 'transfer')} waiting for the other side` : null,
        ].filter(Boolean);
        const message = parts.length ? parts.join(' · ') : 'Nothing more to categorise automatically';
        const hint = r.suggested ? `${plural(r.suggested, 'suggestion')} left for you to confirm.` : undefined;
        if (!r.changes.length) toast(message, { description: hint });
        else toastUndo(message, () => {
          client.transactions.undoAuto.mutate({ entityId, changes: r.changes }).then(refresh, fail);
        }, hint);
      } catch (e) {
        fail(e);
      }
    },
    [client, refresh],
  );

  const unlinkTransfer = useCallback(
    async (t: Transaction) => {
      try {
        await client.transactions.unlinkTransfer.mutate({ id: t.id });
        await refresh();
        toast('Unlinked — both are normal transactions again');
      } catch (e) {
        fail(e);
      }
    },
    [client, refresh],
  );

  const linkTransfer = useCallback(
    async (entityId: string, outId: string, inId: string) => {
      try {
        const legs = await client.transactions.linkTransfer.mutate({ entityId, outId, inId });
        await refresh();
        toastUndo('Linked as a transfer', () => {
          if (legs[0]) client.transactions.unlinkTransfer.mutate({ id: legs[0].id }).then(refresh, fail);
        });
      } catch (e) {
        fail(e);
      }
    },
    [client, refresh],
  );

  return { remove, duplicate, markReviewed, categorise, setStatus, addTag, announceLearned, autoCategorise, unlinkTransfer, linkTransfer };
};
